import React, { useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { colors } from "@zapalacorp/tokens";
import Button, { Color } from "Components/Form/Button";
import CheckboxComponent from "Components/Form/CheckboxComponent/CheckboxComponent";
import InputTextField from "Components/Form/InputTextField";
import { useTranslation } from "react-i18next";
import "./style/style.scss";
import { INITIAL_PAGESIZE } from "../../hooks/useAction";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "12px",
  padding: "0px",
};

const CLOSE_ICON = require("../../../../../../assets/icons/close.svg");

interface IColumn {
  name: string;
  code: string;
  isChecked: boolean;
}
interface IModalPreferenceConfig {
  isOpen?: boolean;
  label?: string;
  onClose?: () => void | Function;
  onSubmit?: (value: any) => void | Function;
  columnList?: IColumn[];
  pageSize?: number;
}
const listCodeDisabled: Array<string> = ["containerId"];
const ModalPreferenceComponent = (props: IModalPreferenceConfig) => {
  const { isOpen = false, onClose, label = "", columnList = [], onSubmit, pageSize = 0 } = props;
  const { t: translate } = useTranslation();
  const [errorRequired, setErrorRequired] = useState<boolean>(false);

  const [dataSourceColumn, setDataSourceColumn] = useState<any>(columnList);
  const [inputValue, setInputValue] = useState<string>(pageSize?.toString());
  const handleCheckboxChange = (code: string) => {
    setDataSourceColumn((prevColumns: any) =>
      prevColumns.map((col: any) =>
        col.code === code ? { ...col, isChecked: !col.isChecked } : col
      )
    );
  };

  const submitForm = () => {
    if (!inputValue) {
      setErrorRequired(true);

      return;
    }
    if (parseInt(inputValue) < 10) {
      NotificationToast.error(translate("minimumPageSizeGreaterThanTen"));
      return;
    }
    setErrorRequired(false);

    if (onSubmit) {
      onSubmit({
        newDataColumn: dataSourceColumn,
        pageSize: Number(inputValue),
      });
    }
  };

  const resetToDefault = () => {
    setInputValue(INITIAL_PAGESIZE.toString());
    setDataSourceColumn((prevColumns: any) =>
      prevColumns.map((col: any) => ({
        ...col,
        isChecked: true,
      }))
    );
  };
  const isDisabledCheckbox = (code: string): boolean => {
    return listCodeDisabled?.includes(code);
  };

  useEffect(() => {
    setInputValue(pageSize?.toString());
  }, [pageSize]);

  useEffect(() => {
    setDataSourceColumn(columnList);
  }, [columnList]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 24px 0px 24px",
          }}
        >
          <div
            style={{
              fontSize: "22px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "28px",
              fontFamily: "Manrope",
              color: colors.gray600,
            }}
          >
            {label}
          </div>
          <IconButton onClick={onClose}>
            <img src={CLOSE_ICON} />
          </IconButton>
        </div>
        {/* Body */}
        <div
          style={{
            padding: "32px 24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: "20px",
            alignSelf: "stretch",
          }}
        >
          <div>
            <InputTextField
              labelClassName="label-page-size-field"
              bordercolor={colors.gray400}
              label={translate("numberOfRowsPerPage")}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setInputValue(event.target.value);
              }}
              value={inputValue}
              required
              error={errorRequired}
              errorMessage={translate("thisFieldIsRequired")}
            />
          </div>

          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
              alignSelf: "stretch",
            }}
          >
            <div
              style={{
                color: colors.brandBlue600,
                fontFamily: "Manrope",
                fontSize: "10px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "15px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {translate("showHideColumn")}
            </div>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                justifyContent: "space-between",
              }}
            >
              {dataSourceColumn?.length > 0 &&
                dataSourceColumn?.map((item: any) => (
                  <Box
                    key={item?.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      width: { xs: "45%", sm: "30%" }, // Responsive: 2 cols on small screens, 3 on larger screens
                      minWidth: "150px",
                    }}
                  >
                    <CheckboxComponent
                      onChange={() => handleCheckboxChange(item.code)}
                      checked={item?.isChecked}
                      label={item?.label}
                      disabled={isDisabledCheckbox(item.code)}
                    />
                  </Box>
                ))}
            </Box>
          </Box>
        </div>
        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #e4e7ec",
          }}
        >
          <div
            style={{
              display: "flex",
              padding: "16px 0px",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
              paddingLeft: "24px",
              paddingRight: "24px",
            }}
          >
            <Button customColor={Color.CTA_BRAND_COLOR_TEXT} onClick={() => resetToDefault()}>
              <span>{translate("resetToDefault")}</span>
            </Button>
            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
              }}
            >
              <Button customColor={Color.GRAY} onClick={onClose}>
                {translate("cancel")}
              </Button>
              <Button customColor={Color.SUCCESS} onClick={submitForm} className="update-btn">
                {translate("update")}
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default ModalPreferenceComponent;
